import { GlobalWorkerOptions, getDocument } from "pdfjs-dist/legacy/build/pdf.mjs";

import { placeSelectionInsideBlock } from "../selection.js";
import { ManuscriptWidget } from "./base.js";

if (!GlobalWorkerOptions.workerSrc) {
    GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
}

export class GraphicsWidget extends ManuscriptWidget {
    constructor(filePath, previewByPath = null, centered = false, figureData = null) {
        super();
        this.filePath = filePath;
        this.previewByPath = previewByPath;
        this.centered = centered;
        this.figureData = figureData;
        this.destroyed = false;
        this.height = 220;
    }

    eq(widget) {
        return (
            widget.filePath === this.filePath &&
            widget.previewByPath === this.previewByPath &&
            widget.centered === this.centered &&
            JSON.stringify(widget.figureData) === JSON.stringify(this.figureData)
        );
    }

    toDOM(view) {
        this.destroyed = false;
        const element = document.createElement("div");
        element.classList.add("ol-cm-environment-figure");
        element.classList.add("ol-cm-environment-line");
        element.classList.toggle("ol-cm-environment-centered", this.centered);

        this.renderGraphic(element, view);

        element.addEventListener("mouseup", (event) => {
            event.preventDefault();
            view.dispatch(placeSelectionInsideBlock(view, event));
        });

        return element;
    }

    updateDOM(element, view) {
        this.destroyed = false;
        element.classList.toggle("ol-cm-environment-centered", this.centered);
        if (
            element.dataset.filepath === this.filePath &&
            element.dataset.width === String(this.figureData?.width?.toString())
        ) {
            return true;
        }
        this.renderGraphic(element, view);
        view.requestMeasure();
        return true;
    }

    ignoreEvent(event) {
        return (
            event.type !== "mouseup" &&
            !(event.target instanceof HTMLElement && event.target.closest(".ol-cm-graphics-edit-button"))
        );
    }

    destroy() {
        this.destroyed = true;
    }

    coordsAt(element) {
        return element.getBoundingClientRect();
    }

    readPhrase(view, key, fallback) {
        const phrase = view?.state?.phrase;
        if (typeof phrase === "function") {
            try {
                return phrase(key) || fallback;
            } catch (_error) {
                return fallback;
            }
        }
        return fallback;
    }

    renderGraphic(element, view) {
        element.textContent = "";
        element.dataset.filepath = this.filePath || "";
        element.dataset.width = this.figureData?.width?.toString() || "";

        const preview = this.previewByPath?.(this.filePath);

        if (!this.filePath || !preview?.url) {
            element.appendChild(this.createMissingPreviewElement());
            return;
        }

        switch ((preview.extension || "").toLowerCase()) {
            case "pdf": {
                const canvas = document.createElement("canvas");
                canvas.classList.add("ol-cm-graphics");
                this.renderPDF(view, canvas, preview.url).catch(() => {
                    if (this.destroyed) {
                        return;
                    }
                    element.replaceChildren(this.createErrorElement(view));
                    this.height = element.clientHeight || this.height;
                    view.requestMeasure();
                });
                element.append(canvas);
                break;
            }
            case "svg":
                element.append(this.createSvgImage(view, preview.url));
                break;
            default:
                element.append(this.createImage(view, preview.url));
                break;
        }
    }

    getFigureWidth() {
        if (this.figureData?.width) {
            return `min(100%, ${this.figureData.width * 100}%)`;
        }
        return "";
    }

    createImage(view, url) {
        const wrapper = document.createElement("div");
        const image = document.createElement("img");
        image.classList.add("ol-cm-graphics", "ol-cm-graphics-loading");
        image.alt = this.filePath;
        const width = this.getFigureWidth();
        image.style.width = width;
        image.style.maxWidth = width;
        image.src = url;

        image.addEventListener("load", () => {
            image.classList.remove("ol-cm-graphics-loading");
            this.height = image.height || this.height;
            view.requestMeasure();
        });

        image.addEventListener("error", () => {
            wrapper.replaceChildren(this.createErrorElement(view));
            this.height = wrapper.clientHeight || this.height;
            view.requestMeasure();
        });

        wrapper.appendChild(image);
        return wrapper;
    }

    createSvgImage(view, url) {
        const wrapper = document.createElement("div");
        const image = document.createElement("img");
        image.classList.add("ol-cm-graphics", "ol-cm-graphics-loading");
        image.alt = this.filePath;
        const width = this.getFigureWidth();
        image.style.width = width;
        image.style.maxWidth = width;

        fetch(url)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch SVG: ${response.status}`);
                }
                return response.text();
            })
            .then((svgText) => {
                if (this.destroyed) {
                    return;
                }

                const blob = new Blob([svgText], { type: "image/svg+xml" });
                const objectUrl = URL.createObjectURL(blob);

                const showError = () => {
                    URL.revokeObjectURL(objectUrl);
                    wrapper.replaceChildren(this.createErrorElement(view));
                    this.height = wrapper.clientHeight || this.height;
                    view.requestMeasure();
                };

                image.addEventListener(
                    "load",
                    () => {
                        URL.revokeObjectURL(objectUrl);
                        image.classList.remove("ol-cm-graphics-loading");
                        this.height = image.height || this.height;
                        view.requestMeasure();
                    },
                    { once: true },
                );
                image.addEventListener("error", showError, { once: true });
                image.src = objectUrl;
            })
            .catch(() => {
                if (this.destroyed) {
                    return;
                }
                wrapper.replaceChildren(this.createErrorElement(view));
                this.height = wrapper.clientHeight || this.height;
                view.requestMeasure();
            });

        wrapper.appendChild(image);
        return wrapper;
    }

    createMissingPreviewElement() {
        const box = document.createElement("div");
        box.className = "ol-cm-graphics-error ol-cm-monospace";
        box.textContent = this.filePath || "Figure";
        return box;
    }

    createErrorElement(view) {
        const wrapper = document.createElement("div");
        wrapper.classList.add("ol-cm-graphics-loading-error");

        const title = document.createElement("span");
        title.classList.add("ol-cm-graphics-loading-error-title");
        title.textContent = this.readPhrase(
            view,
            "the_visual_editor_cant_preview_this_type_of_image_file",
            "Preview unavailable",
        );

        const subtitle = document.createElement("span");
        subtitle.classList.add("ol-cm-graphics-loading-error-subtitle");
        subtitle.textContent = this.readPhrase(
            view,
            "click_recompile_and_check_your_pdf_to_see_how_its_looking",
            "Recompile and check the PDF preview.",
        );

        wrapper.appendChild(title);
        wrapper.appendChild(subtitle);
        return wrapper;
    }

    async renderPDF(view, canvas, url) {
        const pdf = await getDocument(url).promise;
        const page = await pdf.getPage(1);

        if (this.destroyed) {
            return;
        }

        const viewport = page.getViewport({ scale: 1 });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const width = this.getFigureWidth();
        canvas.style.width = width;
        canvas.style.maxWidth = width;
        page.render({
            canvasContext: canvas.getContext("2d"),
            viewport,
        });
        this.height = viewport.height;
        view.requestMeasure();
    }

    get estimatedHeight() {
        return this.height;
    }
}
